// src/components/admin/reseller/ResellerManagement.js
import React, { useEffect, useState } from "react";
import { Box, Button, Alert, Stack, Typography } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { adminAPI } from "../../../services/api";
import { useResellerState } from "./useResellerState";
import ResellerTable from "./ResellerTable";
import ResellerDialog from "./ResellerDialog";
import CreditDialog from "./CreditDialog";

const ResellerManagement = () => {
  const { t } = useTranslation();
  const {
    resellers,
    setResellers,
    error,
    loading,
    submitLoading,
    setSubmitLoading,
    selectedReseller,
    setSelectedReseller,
    availablePackages,
    fetchResellers,
    enqueueSnackbar,
  } = useResellerState();

  const [resellerDialogOpen, setResellerDialogOpen] = useState(false);
  const [isLoading, setIsloading] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditReason, setCreditReason] = useState("");
  const [emailTemplates, setEmailTemplates] = useState([]);

  // In ResellerManagement.js
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    initialCredits: 0,
    markupPercentage: 0,
    allowedPackages: [],
    packagePricing: {}, // Initialize packagePricing as an empty object
  });

  const handleEditClick = (reseller) => {
    setSelectedReseller(reseller);
    // Populate form data with existing reseller data
    setFormData({
      username: reseller.username || "",
      email: reseller.email || "",
      password: "", // Leave password empty when editing
      markupPercentage: reseller.markupPercentage || 0,
      allowedPackages: reseller.allowedPackages || [],
      packagePricing: reseller.packagePricing || {},
    });
    setResellerDialogOpen(true);
  };

  const handleCreditClick = (reseller) => {
    setSelectedReseller(reseller);
    setCreditDialogOpen(true);
  };

  const handleDeleteReseller = async (resellerId) => {
    if (window.confirm(t("reseller.confirmDelete"))) {
      try {
        setIsloading(true);
        await adminAPI.deleteReseller(resellerId);
        await fetchResellers();
        enqueueSnackbar(t("reseller.deleteSuccess"), { variant: "success" });
      } catch (err) {
        enqueueSnackbar(
          err.response?.data?.message || t("reseller.deleteError"),
          {
            variant: "error",
          }
        );
      } finally {
        setIsloading(false);
      }
    }
  };

  const handleCreditSubmit = async () => {
    try {
      const amount = parseFloat(creditAmount);
      if (isNaN(amount)) {
        enqueueSnackbar(t("admin.resellers.invalidAmount"), {
          variant: "error",
        });
        return;
      }

      if (amount < 0 && !creditReason.trim()) {
        enqueueSnackbar(t("admin.resellers.creditReasonRequired"), {
          variant: "error",
        });
        return;
      }

      setSubmitLoading(true);
      const response = await adminAPI.adjustCredits(selectedReseller.id, {
        amount,
        reason: creditReason,
      });

      if (response.data.success) {
        // Ensure we're working with the correct data structure
        const newBalance = parseFloat(response.data.user.creditBalance);

        // Update the local state with the new balance
        setResellers((prevResellers) =>
          prevResellers.map((reseller) =>
            reseller.id === selectedReseller.id
              ? {
                  ...reseller,
                  creditBalance: newBalance,
                }
              : reseller
          )
        );

        enqueueSnackbar(t("admin.resellers.creditAdjustSuccess"), {
          variant: "success",
        });

        // Close dialog and refresh
        handleCloseDialogs();
        await fetchResellers(); // Fetch fresh data from server
      }
    } catch (error) {
      console.error("Credit adjustment error:", error);
      enqueueSnackbar(
        error.response?.data?.message || t("admin.resellers.creditAdjustError"),
        { variant: "error" }
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const validatePassword = (password) => {
    if (!password) return true; // Allow empty password for updates
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  // In ResellerManagement.js where you handle form submit
  const handleResellerSubmit = async () => {
    try {
      // Check if we need to validate password (required for new resellers)
      if (!selectedReseller && !validatePassword(formData.password)) {
        enqueueSnackbar(
          "Password must contain at least 8 characters, including uppercase, lowercase, " +
            "number and special character (@$!%*?&)",
          { variant: "error", autoHideDuration: 6000 }
        );
        return; // Stop submission
      }

      // If updating and password is provided, validate it
      if (
        selectedReseller &&
        formData.password &&
        !validatePassword(formData.password)
      ) {
        enqueueSnackbar(
          "Password must contain at least 8 characters, including uppercase, lowercase, " +
            "number and special character (@$!%*?&)",
          { variant: "error", autoHideDuration: 6000 }
        );
        return; // Stop submission
      }

      setSubmitLoading(true);

      // Log the data being sent

      const data = {
        ...formData,
        packagePricing: formData.packagePricing || {},
      };

      const response = selectedReseller
        ? await adminAPI.updateReseller(selectedReseller.id, data)
        : await adminAPI.createReseller(data);

      if (response.data.success) {
        await fetchResellers();
        enqueueSnackbar(
          t(
            selectedReseller
              ? "reseller.updateSuccess"
              : "reseller.createSuccess"
          ),
          { variant: "success" }
        );
        handleCloseDialogs();
      }
    } catch (error) {
      console.error("Submission error:", error);
      enqueueSnackbar(error.response?.data?.message || t("errors.general"), {
        variant: "error",
        autoHideDuration: 6000,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCloseDialogs = () => {
    setResellerDialogOpen(false);
    setCreditDialogOpen(false);
    setSelectedReseller(null);
    setFormData({
      username: "",
      email: "",
      password: "",
      initialCredits: 0,
      markupPercentage: 0,
      allowedPackages: [],
      packagePricing: {}, // Reset package pricing
    });
    setCreditAmount("");
    setCreditReason("");
  };

  useEffect(() => {
    const fetchEmailTemplates = async () => {
      try {
        const response = await adminAPI.getEmailTemplates();
        setEmailTemplates(response.data);
      } catch (error) {
        enqueueSnackbar(
          error.response?.data?.message || "Failed to fetch email templates",
          {
            variant: "error",
          }
        );
      }
    };

    fetchEmailTemplates();
  }, [enqueueSnackbar]);

  return (
    <Box sx={{ height: 400, width: "100%" }}>
      {isLoading && <Typography>Loading...</Typography>}

      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <h2>{t("admin.resellers.title")}</h2>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setResellerDialogOpen(true)}
        >
          {t("admin.resellers.addButton")}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {resellers.length === 0 ? (
        <Typography variant="body1" align="center">
          {t("admin.resellers.noResellers")}
        </Typography>
      ) : (
        <ResellerTable
          resellers={resellers}
          loading={loading}
          onEdit={handleEditClick}
          onCredit={handleCreditClick}
          onDelete={handleDeleteReseller}
        />
      )}

      <ResellerDialog
        open={resellerDialogOpen}
        onClose={handleCloseDialogs}
        onSubmit={handleResellerSubmit}
        loading={submitLoading}
        editMode={!!selectedReseller}
        formData={formData}
        setFormData={setFormData}
        availablePackages={availablePackages}
        selectedReseller={selectedReseller} // Add this line
        emailTemplates={emailTemplates} // Add this prop
      />

      <CreditDialog
        open={creditDialogOpen}
        onClose={handleCloseDialogs}
        onSubmit={handleCreditSubmit}
        amount={creditAmount}
        setAmount={setCreditAmount}
        reason={creditReason}
        setReason={setCreditReason}
        loading={submitLoading}
      />
    </Box>
  );
};

export default ResellerManagement;
